import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Empty } from "@/components/Kit";
import DateInput from "@/components/DateInput";
import { fmtDate } from "@/lib/date";
import { Plus, Trash2, ChevronLeft, ChevronRight, Palmtree } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["S", "M", "T", "W", "T", "F", "S"];

function monthRange(y, m) {
  return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
}

export default function HolidaysTab() {
  const qc = useQueryClient();
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [start, end] = monthRange(cursor.y, cursor.m);
  const [form, setForm] = useState({ date: "", name: "" });

  const { data } = useQuery({
    queryKey: ["holidays", start, end],
    queryFn: async () => (await api.get("/school/holidays", { params: { start, end } })).data,
  });
  const rules = data?.rules || { sundays: true, second_saturday: true };

  const byDate = useMemo(() => {
    const map = {};
    (data?.generated || []).forEach((h) => (map[h.date] = h));
    (data?.holidays || []).forEach((h) => (map[h.date] = h)); // custom wins
    return map;
  }, [data]);

  const saveRules = useMutation({
    mutationFn: async (next) => (await api.put("/school/holiday-rules", next)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["holidays"] }); toast.success("Holiday rules updated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const add = useMutation({
    mutationFn: async (body) => (await api.post("/school/holidays", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["holidays"] }); setForm({ date: "", name: "" }); toast.success("Holiday added"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/holidays/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["holidays"] }); toast.success("Holiday removed"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const first = new Date(cursor.y, cursor.m, 1).getDay();
  const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const shift = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]" data-testid="holidays-tab">
      {/* Calendar */}
      <div className="ep-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800" onClick={() => shift(-1)} data-testid="hol-prev"><ChevronLeft size={18} /></button>
          <p className="font-head text-sm font-semibold text-white">{MONTHS[cursor.m]} {cursor.y}</p>
          <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800" onClick={() => shift(1)} data-testid="hol-next"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-slate-500">
          {DOW.map((d, i) => <div key={i} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const dstr = iso(new Date(cursor.y, cursor.m, day));
            const h = byDate[dstr];
            const isToday = dstr === iso(now);
            return (
              <div key={i} title={h?.name || ""}
                className={`flex min-h-[54px] flex-col rounded-lg border p-1.5 text-xs ${
                  h ? "border-rose-500/40 bg-rose-500/10" : "border-slate-800 bg-slate-800/30"
                } ${isToday ? "ring-1 ring-indigo-400" : ""}`}
                data-testid={`hol-day-${dstr}`}>
                <span className={h ? "font-semibold text-rose-300" : "text-slate-300"}>{day}</span>
                {h && <span className="mt-0.5 line-clamp-2 leading-tight text-[10px] text-rose-300/80">{h.name}</span>}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          Days shaded red are non-working. Attendance can’t be marked on them unless you tick “mark anyway”.
        </p>
      </div>

      {/* Controls */}
      <div className="space-y-4">
        <div className="ep-card p-5">
          <p className="mb-3 text-sm font-semibold text-white">Automatic holidays</p>
          {[
            ["sundays", "Every Sunday"],
            ["second_saturday", "2nd Saturday of every month"],
          ].map(([key, label]) => (
            <label key={key} className="mb-2 flex cursor-pointer items-center justify-between gap-3 text-sm text-slate-300">
              {label}
              <input type="checkbox" className="h-4 w-4 accent-indigo-500" checked={!!rules[key]}
                data-testid={`hol-rule-${key}`}
                onChange={(e) => saveRules.mutate({ ...rules, [key]: e.target.checked })} />
            </label>
          ))}
        </div>

        <div className="ep-card p-5">
          <p className="mb-3 text-sm font-semibold text-white">Add a holiday</p>
          <div className="space-y-2">
            <DateInput className="ep-input w-full" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="hol-date" />
            <input className="ep-input w-full" placeholder="Reason (e.g. Sankranti, Local festival)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hol-name" />
            <button className="ep-btn w-full justify-center" disabled={!form.date || add.isPending}
              onClick={() => add.mutate({ date: form.date, name: form.name || "Holiday" })} data-testid="hol-add">
              <Plus size={15} /> Add holiday
            </button>
          </div>
        </div>

        <div className="ep-card p-5">
          <p className="mb-3 text-sm font-semibold text-white">School holiday list</p>
          {data?.holidays?.length ? (
            <ul className="space-y-1.5">
              {data.holidays.map((h) => (
                <li key={h.id} className="flex items-center justify-between rounded-lg bg-slate-800/40 px-3 py-2 text-sm" data-testid={`hol-item-${h.date}`}>
                  <span><span className="font-mono text-xs text-slate-400">{fmtDate(h.date)}</span> <span className="text-slate-200">{h.name}</span></span>
                  <button className="text-slate-500 hover:text-rose-400" onClick={() => remove.mutate(h.id)} data-testid={`hol-del-${h.date}`}><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          ) : <Empty><Palmtree size={16} className="mr-1 inline" /> No custom holidays yet</Empty>}
        </div>
      </div>
    </div>
  );
}
